import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useFormik } from 'formik'
import { getUserByToken, requestPassword } from '../core/_requests'
import { activateUser, checkActivationCode, socialLogin } from '../../../api'
import { useAuth } from '../core/Auth'
import { authModel } from './Login'
import { SocialLogins } from '../../../types'
import { toast } from 'react-toastify'
import AppleSignInButton from './AppleLogin'
import { toAbsoluteUrl } from '../../../../_myb/helpers'

const initialValues = {
  email: 'admin@demo.com',
  password: '',
  confirmPassword: ''
}

const activateUserSchema = Yup.object().shape({
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required('Password confirmation is required'),
  email: Yup.string().email('Wrong email format').required('Email is required'),
})

export function ActivateUser() {
  const navigate = useNavigate()
  const { saveAuth, saveCurrentUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams();
  const activationCode = searchParams.get('actCode');
  const [setupPassword, setSetupPassword] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [formSuccess, setFormSuccess] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [activationError, setActivationError] = useState<string>('')
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState)
  }

  const getAccessDetail = async () => {
    try {
      // checkActivationCode returns isAccessSet: true once the account already
      // has a password/social login set up — that account is already activated,
      // so the password form must not be shown (the backend rejects re-activation).
      const isAccessSet = await checkActivationCode(activationCode || '')
      if (isAccessSet) {
        setSetupPassword(false)
        setActivationError("This account has already been activated. Please log in, or use 'Forgot Password' if you don't remember your credentials.".toTranslatedDisplay())
      }
    } catch (error: any) {
      setActivationError('Invalid or expired activation code'.toTranslatedDisplay())
      setSetupPassword(false)
    }
  }

  useEffect(() => {
    getAccessDetail()
  }, [])

  // Replace the previous Google initialization useEffect with this:
  useEffect(() => {
    const themeModeValue = localStorage.getItem('kt_theme_mode_value')
    let pollId: ReturnType<typeof setInterval> | undefined
    let existingScriptRef: HTMLScriptElement | undefined

    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: '925718632458-3e5u51f0ne71jj4qk5ftpo3dk65csf2m.apps.googleusercontent.com',
          callback: handleGoogleActivation,
        })

        // Add a small delay to ensure DOM element exists
        setTimeout(() => {
          const buttonElement = document.getElementById('google-activate-btn')
          if (buttonElement) {
            window.google.accounts.id.renderButton(buttonElement, {
              theme: themeModeValue === 'dark' ? 'filled_black' : 'outline',
              size: 'large',
              text: 'continue_with',
            })
          }
        }, 100)
      } else {
        console.error('Google API not loaded')
      }
    }

    // Load the Google Identity Services SDK if it's not already loaded
    const loadGoogleIdentityScript = () => {
      // index.html already declares this script tag with async/defer, so it's
      // usually found here before it has actually finished loading (especially
      // in incognito, where there's no disk cache to speed up the fetch). Don't
      // just give up if window.google isn't ready yet — wait for its load event,
      // with a short poll as a fallback in case the event fired before we attached.
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[src="https://accounts.google.com/gsi/client"]'
      )
      if (existingScript) {
        if (window.google && window.google.accounts) {
          initializeGoogleSignIn()
          return
        }
        existingScriptRef = existingScript
        existingScript.addEventListener('load', initializeGoogleSignIn, { once: true })
        let attempts = 0
        pollId = setInterval(() => {
          attempts++
          if (window.google && window.google.accounts) {
            clearInterval(pollId)
            initializeGoogleSignIn()
          } else if (attempts > 20) {
            clearInterval(pollId)
          }
        }, 150)
        return
      }

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = initializeGoogleSignIn
      document.body.appendChild(script)
    }

    // Check if the SDK is already present
    if (!window.google) {
      loadGoogleIdentityScript()
    } else {
      initializeGoogleSignIn()
    }

    return () => {
      if (pollId) clearInterval(pollId)
      existingScriptRef?.removeEventListener('load', initializeGoogleSignIn)
    }
  }, [activationCode])

  const formik = useFormik({
    initialValues,
    validationSchema: activateUserSchema,
    onSubmit: async (values) => {
      setLoading(true)
      setHasErrors(undefined)
      try {
        await activateUser(activationCode || '', values.password)
        setFormSuccess(true)
      } catch (error: any) {
        setHasErrors(true)
        toast.error((error?.message || 'Failed to activate account. Please try again.').toTranslatedDisplay())
      } finally {
        setLoading(false)
      }
    },
  })

  const title = (setupPassword) ? 'Activate your Account'.toTranslatedDisplay() : 'Accept Access Request'.toTranslatedDisplay()

  // Update the handleGoogleActivation function to include loading state:
  const handleGoogleActivation = async (response: any) => {
    setGoogleLoading(true);
    try {
      const token = response.credential;

      // Complete activation with social login
      await activateUser(activationCode || '', undefined, SocialLogins.Google, token);
      setFormSuccess(true);
    } catch (error) {
      console.error('Error during Google activation:', error);
      toast.error('Google sign-in failed. Please try again.'.toTranslatedDisplay());
      setGoogleLoading(false);
    }
  };

  // Update the handleAppleActivation function to include loading state:
  const handleAppleActivation = async (appleData: {
    code: string
    id_token: string
    user?: any
    state?: string
  }) => {
    setAppleLoading(true);
    try {
      // Complete activation with social login
      await activateUser(activationCode || '', undefined, SocialLogins.Apple, appleData.id_token);
      setFormSuccess(true);
    } catch (error) {
      console.error('Error during Apple activation:', error);
      toast.error('Apple sign-in failed. Please try again.'.toTranslatedDisplay());
      setAppleLoading(false);
    }
  };

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-gray-900 fw-bolder mb-3'>{title}</h1>
        {formSuccess ? (
          <>
            <div className='mb-10 bg-light-info p-8 rounded'>
              <div className='text-info'>{'User Activated!'.toTranslatedDisplay()}</div>
            </div>
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              <button onClick={() => { navigate('/auth/login') }} className='btn btn-primary'>
                {'Login'.toTranslatedDisplay()}
              </button>
            </div>
          </>
        ) : activationError ? (
          <>
            <div className='mb-10 bg-light-danger p-8 rounded'>
              <div className='text-danger fw-bold'>{activationError}</div>
            </div>
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              <Link to='/auth/login'>
                <button type='button' className='btn btn-primary'>{'Go to Login'.toTranslatedDisplay()}</button>
              </Link>
            </div>
          </>
        ) : (
          <>
            {setupPassword && (
              <>
                {/* Rest of your existing form content remains the same */}
                <div className='text-gray-500 fw-semibold fs-6'>{'Enter your password or continue with google to activate your account'.toTranslatedDisplay()}</div>
                <div className='text-gray-500 fw-semibold fs-6'>{'Enter your password or continue with google to activate your account'.toTranslatedDisplay()}</div>
                {/* Add this section after "Enter your password or continue with google..." text */}
                <div className='mb-8'>


                  <div className='mt-3 row g-3'>
                    <div className='col-md-6' style={{ colorScheme: "light" }}>
                      <div id="google-activate-btn"></div>
                    </div>


                    <AppleSignInButton
                      toAbsoluteUrl={toAbsoluteUrl}
                      onAppleSignIn={handleAppleActivation}
                      className="btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100"
                    />

                  </div>
                </div>

                {/* Password Field */}
                <div className='fv-row mb-8' data-kt-password-meter='true'>
                  <div className='mb-1'>
                    <label className='form-label fw-bolder text-gray-900 fs-6'>{'Password'.toTranslatedDisplay()}</label>
                    <div className='position-relative mb-3'>
                      <div className="input-group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={'Password'.toTranslatedDisplay()}
                          autoComplete='off'
                          {...formik.getFieldProps('password')}
                          className={clsx(
                            'form-control bg-transparent',
                            {
                              'is-invalid': formik.touched.password && formik.errors.password,
                            },
                            {
                              'is-valid': formik.touched.password && !formik.errors.password,
                            }
                          )}
                        />
                        <span className="input-group-text" onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                          {showPassword ? (
                            <i className="bi bi-eye-slash"></i>
                          ) : (
                            <i className="bi bi-eye"></i>
                          )}
                        </span>
                      </div>
                      {formik.touched.password && formik.errors.password && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.password?.toTranslatedDisplay()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='text-muted'>{'Use 8 or more characters with a mix of letters, numbers & symbols.'.toTranslatedDisplay()}</div>
                </div>

                {/* Confirm Password Field */}
                <div className='fv-row mb-5'>
                  <label className='form-label fw-bolder text-gray-900 fs-6'>{'Confirm Password'.toTranslatedDisplay()}</label>
                  <div className="input-group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={'Password confirmation'.toTranslatedDisplay()}
                      autoComplete='off'
                      {...formik.getFieldProps('confirmPassword')}
                      className={clsx(
                        'form-control bg-transparent',
                        {
                          'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword,
                        },
                        {
                          'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
                        }
                      )}
                    />
                    <span className="input-group-text" onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                      {showPassword ? (
                        <i className="bi bi-eye-slash"></i>
                      ) : (
                        <i className="bi bi-eye"></i>
                      )}
                    </span>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.confirmPassword?.toTranslatedDisplay()}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                  <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>

                    {loading ? (
                      <span className='indicator-progress' style={{ display: 'block' }}>
                        {'Please wait...'.toTranslatedDisplay()}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    ) :
                      <span className='indicator-label'>{'Activate Account'.toTranslatedDisplay()}</span>
                    }
                  </button>
                  <Link to='/auth/login'>
                    <button
                      type='button'
                      id='kt_login_password_reset_form_cancel_button'
                      className='btn btn-light'
                      disabled={formik.isSubmitting || !formik.isValid}
                    >{'Cancel'.toTranslatedDisplay()}</button>
                  </Link>
                </div>
              </>
            )}
          </>
        )}



      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {'Sorry, looks like there are some errors detected, please try again.'.toTranslatedDisplay()}
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>{'Sent password reset. Please check your email'.toTranslatedDisplay()}</div>
        </div>
      )}


    </form>
  )
}
